import {Button} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {ReactNode} from 'react';
import {createPortal} from 'react-dom';

import {isNil, isNotNil} from 'ramda';

import {TelephonyCall} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {formatCallDuration} from '@dms/shared';

import {DOT_CHARACTER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useCallElapsedSeconds} from '../../hooks/useCallElapsedSeconds';
import {useCallPanel} from '../../hooks/useCallPanel';
import {getCallPhoneNumberLabel} from '../../utils/getCallPhoneNumberLabel';
import {TelephonyCallIndicatorAppearance} from '../../utils/getTelephonyCallIndicatorAppearance';
import {CallCustomerActions} from '../TelephonyCallIndicator/components/CallCustomerActions';
import {OutboundMobileSteps} from '../TelephonyCallIndicator/components/OutboundMobileSteps';
import {TelephonyCallPanelCaller} from './TelephonyCallPanelCaller';

interface TelephonyCallPanelLiveProps extends RequiredTestIdProps {
  call: TelephonyCall;
  appearance: TelephonyCallIndicatorAppearance;
  isRingingHere: boolean;
  browserCall: {isMuted: boolean} | null;
  onAccept: VoidFunction;
  onReject: VoidFunction;
  onToggleMute: VoidFunction;
  onHangUp: VoidFunction;
  onOpenCustomer: (customerId: string) => void;
  onCreateCustomer: VoidFunction;
  onFindCustomer: VoidFunction;
  availabilityMenu: ReactNode;
}

/**
 * The live call at the top of the call panel: caller, status and controls. Ended calls
 * are shown by the panel's pairing part instead, so the caller never shows twice.
 */
export function TelephonyCallPanelLive(props: TelephonyCallPanelLiveProps) {
  const {state: panel} = useCallPanel();
  const elapsedSeconds = useCallElapsedSeconds(props.call.connectedAt);

  const isTalkingOnMobile =
    props.call.status === 'in_call' && isNil(props.browserCall) && props.call.channel !== 'browser';
  const status =
    props.call.status === 'in_call'
      ? i18n.t('page.telephony.panel.onCall', {duration: formatCallDuration(elapsedSeconds ?? 0)})
      : props.appearance.label;

  return (
    <>
      <TelephonyCallPanelCaller
        call={props.call}
        status={status}
        dotColor={props.appearance.dotColor}
        actions={
          <>
            <Show when={props.isRingingHere}>
              <Button
                variant="dangerOutlined"
                size="small"
                title={i18n.t('general.actions.reject')}
                onClick={props.onReject}
                data-testid={suffixTestId('reject', props)}
              />
              <Button
                variant="success"
                size="small"
                leftIcon="communication/call"
                title={i18n.t('general.actions.accept')}
                onClick={props.onAccept}
                data-testid={suffixTestId('accept', props)}
              />
            </Show>
            <Show when={props.browserCall}>
              {(browserCall) => (
                <>
                  <Button
                    variant="secondary"
                    size="small"
                    title={
                      browserCall.isMuted
                        ? i18n.t('page.telephony.browser.unmute')
                        : i18n.t('page.telephony.browser.mute')
                    }
                    onClick={props.onToggleMute}
                    data-testid={suffixTestId('mute', props)}
                  />
                  <Button
                    variant="danger"
                    size="small"
                    title={i18n.t('page.telephony.browser.hangUp')}
                    onClick={props.onHangUp}
                    data-testid={suffixTestId('hangUp', props)}
                  />
                </>
              )}
            </Show>
            <CallCustomerActions
              call={props.call}
              onOpenCustomer={props.onOpenCustomer}
              onCreateCustomer={props.onCreateCustomer}
              onFindCustomer={props.onFindCustomer}
              data-testid={suffixTestId('panel', props)}
            />
          </>
        }
      >
        <Show when={isTalkingOnMobile}>
          <Text size="small" color="secondary">
            {[
              i18n.t('page.telephony.indicator.audioOnMobile'),
              i18n.t('page.telephony.bar.endOnMobile'),
            ].join(` ${DOT_CHARACTER} `)}
          </Text>
        </Show>
        {match(props.call)
          .with(
            {
              direction: 'outbound',
              channel: 'mobile',
              status: Pattern.union(
                'ringing_user',
                'waiting_for_confirmation',
                'connecting_customer'
              ),
            },
            (outboundCall) => (
              <OutboundMobileSteps
                status={outboundCall.status}
                customer={outboundCall.customerName ?? getCallPhoneNumberLabel(outboundCall)}
                direction="column"
                isInverted={false}
                data-testid={suffixTestId('panel', props)}
              />
            )
          )
          .otherwise(() => null)}
      </TelephonyCallPanelCaller>
      <Show
        when={
          props.call.status === 'in_call' && isNotNil(props.availabilityMenu)
            ? panel.footerSlot
            : null
        }
      >
        {(footerSlot) =>
          createPortal(
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('page.telephony.indicator.availabilityAfterCall')}
              </Text>
              {props.availabilityMenu}
            </VStack>,
            footerSlot
          )
        }
      </Show>
    </>
  );
}
